import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Dialog, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import JsBarcode from 'jsbarcode';
import requests from '../../api/client';
import { normalizeColor } from '../../Brand';
import { usePixPolling } from '../../hooks';
import { interpolate, useTranslation } from '../../i18n';
import { cardSx, colors, radius } from '../../theme/tokens';
import type { LegacyPaymentData, LegacyProductType, PaymentStatusKind } from '../../types/api';
import { copyToClipboard } from '../../utils/clipboard';
import { useFeedback } from '../common/Feedback';
import { Loading } from '../common/Loading';

/**
 * Fluxo A (legado) — `/renova_plano?t={tipo}&id={id}`.
 *
 * Esta tela é consumida por OUTRO produto da Nogle (o site "Link de Venda
 * Direta"), que redireciona o cliente para cá com os parâmetros na query.
 * A URL, os parâmetros e o que a tela exibe são contrato externo: nada disso
 * pode mudar. A referência de paridade é `resources/views/pagamento.blade.php`
 * + `PagamentoController::indexView` na versão Laravel (commit b9b5dd9).
 *
 * Diferença essencial em relação ao resto do SPA: **aqui não vale a identidade
 * do Recarga Fácil**. Cor, logo e nome vêm no próprio payload de pagamento
 * (a operadora dona da venda), e são aplicados só via `sx` — as CSS custom
 * properties globais da marca continuam intactas.
 */

/**
 * Logo Contel embarcado, usado quando a operadora é a Contel e o ERP não
 * devolve logo (ou o logo devolvido quebra). Equivale ao
 * `asset('assets/img/logo_contel_default.png')` do Blade.
 */
export const DEFAULT_CONTEL_LOGO = '/images/logo-contel-default.png';

/**
 * Suporte fixo da Contel. No legado isso era forçado no controller
 * (`if (strtolower($operadora) === 'contel') $suporte = 'https://wa.me/...'`),
 * sobrescrevendo o que viesse da API. Como não há mais controller, a regra
 * vive aqui — sem ela, o cliente Contel cairia num suporte errado.
 */
export const CONTEL_SUPPORT_URL = 'https://wa.me/558007173774';

/**
 * Ilustrações de rodapé exclusivas da Contel.
 *
 * Na versão Blade elas vinham da classe `.figures`, aplicada à seção só quando
 * `strtolower($operadora) === 'contel'` (`pagamento.blade.php:40`). São
 * decorativas: não carregam informação e por isso não têm texto alternativo.
 */
const CONTEL_FIGURE_LEFT = '/images/ilustra-left.png';
const CONTEL_FIGURE_RIGHT = '/images/ilustra-right.png';

/**
 * Cor de fundo quando o ERP não devolve `cor`. É o `corPadraoDeFundo` do
 * antigo `public/assets/js/app.js`; não é token de marca do Recarga Fácil e
 * por isso não sai de `theme/tokens.ts`.
 */
const FALLBACK_BACKGROUND = '#144ACE';

/** Tempo que o botão fica exibindo "Copiado!". */
const COPY_FEEDBACK_MS = 2000;

/** Valores aceitos em `?t=` — o enum `App\Http\Enum\Produto` do legado. */
const PRODUCT_TYPES: readonly LegacyProductType[] = ['R', 'A', 'C'];

/** Mapeia o `?t=` para a chave de tradução do nome do produto. */
const PRODUCT_LABEL_KEY: Record<LegacyProductType, 'recarga' | 'ativacao' | 'compra'> = {
  R: 'recarga',
  A: 'ativacao',
  C: 'compra',
};

type LoadStatus = 'loading' | 'ready' | 'error';
type CopyTarget = 'pix' | 'boleto';

/**
 * Tabela consultada pelo polling, por tipo de solicitação. `R` (recarga) e `A`
 * (ativação) vivem em `OPERADORA_RECARGA` ⇒ `ATIVREC`; `C` (compra) vive em
 * `OPERADORA_PEDIDO` ⇒ `LOGISTICA`. Espelha exatamente a escolha de tabela do
 * `FinanceiroController::pagamento`.
 */
const PAYMENT_STATUS_KIND: Record<LegacyProductType, PaymentStatusKind> = {
  R: 'ATIVREC',
  A: 'ATIVREC',
  C: 'LOGISTICA',
};

function parseProductType(value: string | null): LegacyProductType | null {
  if (!value) return null;
  return (PRODUCT_TYPES as readonly string[]).includes(value)
    ? (value as LegacyProductType)
    : null;
}

/** `explode(" ", $nome)[0]` do Blade. */
function firstName(fullName: string | null | undefined): string {
  return String(fullName ?? '').trim().split(/\s+/)[0] ?? '';
}

/** Normaliza campos opcionais do payload: string vazia conta como ausente. */
function nonEmpty(value: string | null | undefined): string | null {
  const text = String(value ?? '').trim();
  return text ? text : null;
}

/**
 * Código de barras do boleto.
 *
 * O legado renderizava com JsBarcode dentro de um `<svg data-codigo>`; aqui a
 * mesma lib desenha no SVG referenciado. Se o código não for válido para
 * CODE128, cai para o texto puro — o cliente ainda consegue copiar e digitar.
 */
function Barcode({ code }: { code: string }) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !code) return;

    try {
      JsBarcode(svg, code, {
        format: 'CODE128',
        displayValue: true,
        width: 2,
        height: 80,
        fontSize: 16,
        margin: 10,
        valid: (isValid: boolean) => setFailed(!isValid),
      });
    } catch {
      setFailed(true);
    }
  }, [code]);

  if (failed) {
    return (
      <Typography
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          wordBreak: 'break-all',
          color: colors.textPrimary,
        }}
      >
        {code}
      </Typography>
    );
  }

  return <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block' }} />;
}

export function LegacyPayment() {
  const { t } = useTranslation();
  const { show } = useFeedback();
  const [searchParams] = useSearchParams();

  const productType = parseProductType(searchParams.get('t'));
  const requestId = nonEmpty(searchParams.get('id'));

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [data, setData] = useState<LegacyPaymentData | null>(null);

  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState<CopyTarget | null>(null);
  const copyTimeout = useRef<number | null>(null);

  const { status: pollingStatus, start: startPolling, stop: stopPolling } = usePixPolling();
  /** Evita reabrir o diálogo de confirmação se a tela re-renderizar. */
  const confirmedRef = useRef(false);

  // ─── Busca ───────────────────────────────────────────────────────────────

  useEffect(() => {
    // Query incompleta ou `t` fora de R/A/C: o legado devolvia `errors/500`
    // com "Falha ao localizar o pagamento." — mesma tela aqui.
    if (!productType || !requestId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    requests
      .legacyPayment({ t: productType, id: requestId })
      .then((list) => {
        if (cancelled) return;
        // O controller lia `['dados'][0]`; lista vazia estourava a exceção e
        // caía na tela de erro.
        const payment = list?.[0];
        if (!payment) {
          setStatus('error');
          return;
        }
        setData(payment);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [productType, requestId]);

  // ─── Dados derivados ─────────────────────────────────────────────────────

  const operadora = data?.operadora ?? '';
  const isContel = operadora.toLowerCase() === 'contel';

  const background = normalizeColor(data?.cor, FALLBACK_BACKGROUND);

  const logoSrc = useMemo(() => {
    const logo = nonEmpty(data?.logo);
    if (logo) return logo;
    // Sem logo no ERP: só a Contel tem substituto local; as demais marcas
    // simplesmente não exibem imagem, como no Blade.
    return isContel ? DEFAULT_CONTEL_LOGO : null;
  }, [data?.logo, isContel]);

  const pixCode = nonEmpty(data?.pix_copia_cola);
  const pixQrCode = nonEmpty(data?.pix_QRCode);
  const boletoLink = nonEmpty(data?.boleto_link);
  const boletoBarcode = nonEmpty(data?.boleto_codigo_barras);
  const paymentLink = nonEmpty(data?.link_pagamento);

  const hasPix = Boolean(pixCode || pixQrCode);
  const hasBoleto = Boolean(boletoLink || boletoBarcode);

  const supportUrl = useMemo(() => {
    // A Contel sempre aponta para o WhatsApp oficial (regra do controller).
    if (isContel) return CONTEL_SUPPORT_URL;
    const support = nonEmpty(data?.suporte);
    if (!support) return null;
    // O Blade prefixava `https://` quando o valor não continha "http".
    return support.includes('http') ? support : `https://${support}`;
  }, [isContel, data?.suporte]);

  // ─── Efeitos colaterais de apresentação ──────────────────────────────────

  useEffect(() => {
    // O `<title>` da view legada era o nome da operadora. Roda depois do
    // BrandProvider (que também escreve o título), então prevalece.
    if (operadora) document.title = operadora;
  }, [operadora]);

  useEffect(() => {
    return () => {
      if (copyTimeout.current !== null) window.clearTimeout(copyTimeout.current);
    };
  }, []);

  // ─── Polling do PIX ──────────────────────────────────────────────────────

  /**
   * No Blade o polling só disparava quando o primeiro segmento da URL era uma
   * marca — o que nunca acontece em `/renova_plano`, ou seja, estava morto.
   * Agora o tenant é resolvido pelo `x-caller-url`, então a confirmação
   * automática passa a funcionar também nesta tela (melhoria deliberada).
   *
   * O `tipo` da consulta **tem** de acompanhar o `t` da URL. O
   * `FinanceiroController::pagamento` devolve, para `t=R` e `t=A`, um
   * `id_OPERADORA_RECARGA`, e para `t=C` um `id_OPERADORA_PEDIDO`. Como as duas
   * sequências de id são independentes, consultar com o tipo errado encontra a
   * solicitação de outra pessoa e confirmaria um pagamento que não aconteceu.
   */
  useEffect(() => {
    if (status !== 'ready' || !hasPix || !productType) return;
    const solicitacaoId = data?.id;
    if (solicitacaoId === undefined || solicitacaoId === null || solicitacaoId === '') return;

    startPolling(solicitacaoId, PAYMENT_STATUS_KIND[productType]);
    return stopPolling;
  }, [status, hasPix, productType, data?.id, startPolling, stopPolling]);

  useEffect(() => {
    if (pollingStatus !== 'paid' || confirmedRef.current) return;
    confirmedRef.current = true;
    show({
      kind: 'success',
      title: t.pix.confirmedTitle,
      text: t.pix.confirmedBody,
      blocking: true,
    });
  }, [pollingStatus, show, t]);
  // No timeout apenas paramos: o cliente pode ter chegado por link e um alerta
  // agressivo confundiria mais do que ajudaria (o hook já encerra sozinho).

  // ─── Ações ───────────────────────────────────────────────────────────────

  const handleCopy = useCallback(async (target: CopyTarget, value: string) => {
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopied(target);
    if (copyTimeout.current !== null) window.clearTimeout(copyTimeout.current);
    copyTimeout.current = window.setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
  }, []);

  const handleLogoError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const image = event.currentTarget;
      if (isContel) {
        // Logo da Contel quebrado: troca pelo arquivo local (sem repetir, para
        // não entrar em laço caso o próprio fallback falhe).
        if (image.dataset.fallbackApplied === 'true') return;
        image.dataset.fallbackApplied = 'true';
        image.src = DEFAULT_CONTEL_LOGO;
        return;
      }
      image.style.display = 'none';
    },
    [isContel]
  );

  // ─── Estilos derivados do payload ────────────────────────────────────────

  /** Botões herdam a cor da operadora, não a cor da marca do Recarga Fácil. */
  const actionButtonSx = useMemo(
    () => ({
      backgroundColor: background,
      color: colors.white,
      fontWeight: 700,
      textTransform: 'none' as const,
      borderRadius: radius.sm,
      boxShadow: 'none',
      px: 3,
      py: 1.25,
      '&:hover': { backgroundColor: background, filter: 'brightness(0.9)', boxShadow: 'none' },
    }),
    [background]
  );

  const cardHeadingSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: { xs: '1rem', md: '1.5rem' },
    lineHeight: 1.2,
  } as const;

  const blockCardSx = { ...cardSx, p: { xs: 3, md: 4 }, mt: 4 } as const;

  // ─── Render ──────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.bgPage,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading />
      </Box>
    );
  }

  if (status === 'error' || !data) {
    // Equivalente ao `errors/500`: fundo azul legado, logo Contel e cartão
    // branco com "Oops!" + a mensagem de falha. A view antiga trazia as
    // ilustrações de rodapé aqui INCONDICIONALMENTE (errors/500.blade.php),
    // diferente da tela de pagamento, onde só apareciam para a Contel.
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          backgroundColor: FALLBACK_BACKGROUND,
          backgroundImage: `url("${CONTEL_FIGURE_LEFT}"), url("${CONTEL_FIGURE_RIGHT}")`,
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'left bottom, right bottom',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          px: 2,
          py: 6,
        }}
      >
        <Box
          component="img"
          src={DEFAULT_CONTEL_LOGO}
          alt=""
          sx={{ maxWidth: 220, width: '100%', height: 'auto' }}
        />
        <Box sx={{ ...cardSx, p: { xs: 3, md: 4 }, maxWidth: 640, width: '100%', textAlign: 'center' }}>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, color: colors.textPrimary }}
          >
            {t.common.oops}
          </Typography>
          <Typography
            sx={{ mt: 1, fontSize: { xs: '1rem', md: '1.25rem' }, color: colors.textSecondary }}
          >
            {t.legacyPayment.notFound}
          </Typography>
        </Box>
      </Box>
    );
  }

  const isPaid = pollingStatus === 'paid';
  const productLabel = productType ? t.legacyPayment.product[PRODUCT_LABEL_KEY[productType]] : '';

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        backgroundColor: background,
        display: 'flex',
        flexDirection: 'column',
        // Só a Contel tem as ilustrações de rodapé (a antiga classe `.figures`).
        ...(isContel
          ? {
              backgroundImage: `url("${CONTEL_FIGURE_LEFT}"), url("${CONTEL_FIGURE_RIGHT}")`,
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left bottom, right bottom',
              backgroundAttachment: 'scroll, scroll',
            }
          : null),
      }}
    >
      <Box component="header" sx={{ py: 3, px: 2, textAlign: 'center' }}>
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={operadora}
            onError={handleLogoError}
            sx={{ maxWidth: 260, width: '100%', height: 'auto', mx: 'auto', display: 'block' }}
          />
        ) : null}
      </Box>

      <Box sx={{ flexGrow: 1, width: '100%', maxWidth: 960, mx: 'auto', px: 2, pb: 6 }}>
        <Typography
          component="h1"
          sx={{
            color: colors.white,
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: '3rem' },
            lineHeight: 1.15,
          }}
        >
          {interpolate(t.legacyPayment.greeting, { name: firstName(data.nome) })}
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: colors.white,
            fontWeight: 700,
            fontSize: { xs: '1.125rem', md: '2rem' },
            mt: 1,
          }}
        >
          {interpolate(t.legacyPayment.subtitle, { product: productLabel })}
        </Typography>

        {isPaid ? (
          // Pagamento confirmado pelo polling: os blocos de pagamento somem
          // para o cliente não tentar pagar de novo.
          <Box sx={{ ...blockCardSx, textAlign: 'center' }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 64, color: colors.success }} />
            <Typography
              component="h3"
              sx={{ mt: 1, fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.5rem' }, color: colors.textPrimary }}
            >
              {t.pix.confirmedTitle}
            </Typography>
            <Typography sx={{ mt: 1, color: colors.textSecondary }}>
              {t.pix.confirmedBody}
            </Typography>
          </Box>
        ) : (
          <>
            {hasPix ? (
              <Box sx={blockCardSx}>
                <Typography component="h3" sx={cardHeadingSx}>
                  <AccessTimeRoundedIcon fontSize="inherit" />
                  {t.legacyPayment.pixDelay}
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 4,
                    alignItems: 'start',
                  }}
                >
                  {pixCode ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                        <Box component="span" sx={{ color: colors.success }}>
                          {t.legacyPayment.pixCopyIntroHighlight}
                        </Box>{' '}
                        {t.legacyPayment.pixCopyIntro}
                      </Typography>

                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          backgroundColor: colors.surfaceMuted,
                          border: `1px solid ${colors.border}`,
                          borderRadius: radius.sm,
                          fontSize: '0.8125rem',
                          color: colors.textPrimary,
                          wordBreak: 'break-all',
                          textAlign: 'left',
                        }}
                      >
                        {pixCode}
                      </Box>

                      <Button
                        type="button"
                        variant="contained"
                        onClick={() => handleCopy('pix', pixCode)}
                        sx={{ ...actionButtonSx, mt: 2 }}
                      >
                        {copied === 'pix' ? t.common.copied : t.legacyPayment.pixCopyButton}
                      </Button>
                    </Box>
                  ) : null}

                  {pixQrCode ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                        <Box component="span" sx={{ color: colors.success }}>
                          {t.legacyPayment.pixScanHighlight}
                        </Box>{' '}
                        {t.legacyPayment.pixScanIntro}
                      </Typography>

                      <Box
                        component="button"
                        type="button"
                        onClick={() => setQrOpen(true)}
                        aria-label={t.legacyPayment.pixQrAlt}
                        sx={{
                          mt: 2,
                          mx: 'auto',
                          display: 'block',
                          width: '60%',
                          minWidth: 160,
                          p: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          component="img"
                          src={pixQrCode}
                          alt={t.legacyPayment.pixQrAlt}
                          sx={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            ) : null}

            {hasBoleto ? (
              <Box sx={blockCardSx}>
                <Typography component="h3" sx={cardHeadingSx}>
                  <AccessTimeRoundedIcon fontSize="inherit" />
                  {t.legacyPayment.boletoDelay}
                </Typography>

                {boletoBarcode ? (
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Barcode code={boletoBarcode} />
                  </Box>
                ) : null}

                <Box
                  sx={{
                    mt: 4,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: boletoLink && boletoBarcode ? '1fr 1fr' : '1fr' },
                    gap: 2,
                    justifyItems: 'center',
                  }}
                >
                  {boletoLink ? (
                    <Button
                      component="a"
                      href={boletoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      sx={actionButtonSx}
                    >
                      {t.legacyPayment.boletoView}
                    </Button>
                  ) : null}

                  {boletoBarcode ? (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={() => handleCopy('boleto', boletoBarcode)}
                      sx={actionButtonSx}
                    >
                      {copied === 'boleto' ? t.common.copied : t.legacyPayment.boletoCopy}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            ) : null}

            {paymentLink ? (
              <Box sx={{ ...blockCardSx, textAlign: 'center' }}>
                <Typography component="h3" sx={cardHeadingSx}>
                  {t.legacyPayment.linkIntro}
                </Typography>

                <Button
                  component="a"
                  href={paymentLink}
                  variant="contained"
                  sx={{ ...actionButtonSx, mt: 4 }}
                >
                  {t.legacyPayment.linkButton}
                </Button>
              </Box>
            ) : null}
          </>
        )}

        {supportUrl ? (
          <Typography
            sx={{
              mt: 4,
              color: colors.white,
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            {t.legacyPayment.supportPrefix}{' '}
            <Box
              component="a"
              href={supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: colors.white, fontWeight: 800, textDecoration: 'underline' }}
            >
              {t.legacyPayment.supportLink}
            </Box>
          </Typography>
        ) : null}
      </Box>

      <Box component="footer" sx={{ py: 3, px: 2, textAlign: 'center' }}>
        <Typography sx={{ color: colors.white, fontSize: '0.875rem' }}>
          {t.legacyPayment.rightsReserved}
        </Typography>
      </Box>

      {pixQrCode ? (
        <Dialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          maxWidth="xs"
          fullWidth
          aria-label={t.legacyPayment.pixQrAlt}
          slotProps={{ paper: { sx: { borderRadius: radius.lg, p: 2 } } }}
        >
          <Box
            component="img"
            src={pixQrCode}
            alt={t.legacyPayment.pixQrAlt}
            sx={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Dialog>
      ) : null}
    </Box>
  );
}
